import { ChangeDetectionStrategy, Component, computed, input, output } from "@angular/core";

import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { InstitutionAccountView } from "@bitwarden/web-vault/app/models/view/institution/institution-account.view";
import { isOther } from "@bitwarden/web-vault/app/models/constants/global.constants";

@Component({
  selector: "app-institutional-accounts",
  templateUrl: "./institutional-accounts.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstitutionalAccountsComponent {
  // Inputs
  institution = input<InstitutionView>();
  isAppendRatesRange = input<boolean>();
  preselectedInstitutionalAccount = input<InstitutionAccountView>();
  isRequired = input<boolean>(false);
  placeholder = input<string>();
  disabled = input<boolean>(false);
  customShadowClass = input<string>();
  showGradientBorder = input<boolean>(true);

  // Output
  selectedInstitutionAccount = output<InstitutionAccountView>();

  // Computed signals
  institutionAccounts = computed(() => {
    const inst = this.institution();
    const appendRates = this.isAppendRatesRange();

    if (!inst) {
      return [];
    }

    const uniqueRawAccounts = inst.availableAccounts.filter(
      (account, index, self) => self.findIndex((a) => a.name === account.name) === index,
    );

    return !appendRates ? uniqueRawAccounts : this.getRateRangeAppendedAccounts(uniqueRawAccounts);
  });

  institutionAccountNames = computed(() =>
    this.institutionAccounts().map((account) => account.name),
  );

  getRateRangeAppendedAccounts(
    institutionAccounts: InstitutionAccountView[],
  ): InstitutionAccountView[] {
    return institutionAccounts.map((institutionAccount) => {
      const rate = isOther(institutionAccount.name) ? "" : this.getRate(institutionAccount);

      return {
        ...institutionAccount,
        name: [institutionAccount.name, rate].filter(Boolean).join(" "),
      };
    });
  }

  /**
   * Calculates the formatted interest rate or range of rates for a given set of rates.
   * This method determines the maximum and minimum rates from the provided array of rate objects,
   * and formats the final rate as a string. If there is more than one rate, it formats the rate as a range (min - max).
   * Otherwise, it returns the single rate. The rate is appended with "P.A." to denote per annum.
   *
   * @returns {string} The formatted interest rate or range of rates per annum.
   * @param data
   */
  getRate(data: InstitutionAccountView) {
    if (data.interestRates) {
      const rate = data.interestRates;
      if (rate.length === 0) {
        return "";
      }

      const maxRate = rate.reduce((max: number, current: { rate: number }) => {
        return current.rate > max ? current.rate : max;
      }, rate[0].rate);
      const minRate = rate.reduce((min: number, current: { rate: number }) => {
        return current.rate < min ? current.rate : min;
      }, rate[0].rate);

      const finalRate = rate.length > 1 ? `${minRate}% - ${maxRate}%` : `${maxRate}%`;
      return `(${finalRate} p.a.)`;
    } else {
      return "";
    }
  }

  institutionAccountSelected(selectedName: string) {
    const selectedAccount = this.institutionAccounts().find(
      (account) => account.name === selectedName,
    );
    if (selectedAccount) {
      this.selectedInstitutionAccount.emit(selectedAccount);
    }
  }

  selectionCleared() {
    this.selectedInstitutionAccount.emit(null);
  }
}
